import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useProjectAuth } from './useProjectAuth'
import { getCached, setCached, invalidate } from '../lib/queryCache'
import * as XLSX from 'xlsx'

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)
const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
)
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
    <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/>
  </svg>
)
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)
const GridIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
  </svg>
)
const ListIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
    <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
    <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
  </svg>
)

const Toast = ({ message, type, onClose }) => (
  <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl text-sm font-medium
    ${type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
    {type === 'success' ? <CheckIcon /> : <XIcon />}
    <span>{message}</span>
    <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100"><XIcon /></button>
  </div>
)

const fmt = (n) => n ? Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'

const tradeColors = [
  'bg-[#D1D5DB] text-[#1A7A4A] border-[#1A7A4A]/30',
  'bg-[#D1D5DB] text-[#0F1017] border-purple-200',
  'bg-emerald-100 text-emerald-700 border-emerald-200',
  'bg-amber-100 text-amber-700 border-amber-200',
  'bg-rose-100 text-rose-700 border-rose-200',
  'bg-cyan-100 text-cyan-700 border-cyan-200',
  'bg-[#D1D5DB] text-[#1A7A4A] border-indigo-200',
  'bg-orange-100 text-orange-700 border-orange-200',
]

// ── Edit Item Modal — full-page style modal for editing a single BOQ line ──
const EditItemModal = ({ item, trades, costCodesList, onClose, onSaved, showToast }) => {
  const [form, setForm] = useState({
    item_no: item.item_no || '',
    description: item.description || '',
    unit: item.unit || '',
    qty_tendered: item.qty_tendered || '',
    selling_unit_rate: item.selling_unit_rate || '',
    package_name: item.package_name || '',
    cost_code: item.cost_code || '',
  })
  const [saving, setSaving] = useState(false)

  const h = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const qty = parseFloat(form.qty_tendered) || 0
  const rate = parseFloat(form.selling_unit_rate) || 0
  const total = qty * rate

  const handleSave = async () => {
    if (!form.description.trim()) return showToast('الوصف مطلوب', 'error')
    setSaving(true)
    const { error } = await supabase.from('boq_items').update({
      item_no: form.item_no || null,
      description: form.description,
      unit: form.unit || null,
      qty_tendered: qty,
      selling_unit_rate: rate,
      package_name: form.package_name || null,
      cost_code: form.cost_code || null,
    }).eq('id', item.id)
    setSaving(false)
    if (error) return showToast('فشل التعديل: ' + error.message, 'error')
    showToast('تم الحفظ')
    onSaved()
  }

  const fieldStyle = { width:'100%', padding:'9px 12px', border:'1px solid #D1D5DB', borderRadius:8, fontSize:13, outline:'none' }
  const labelStyle = { fontSize:11, fontWeight:600, color:'#6B7280', marginBottom:5, display:'block' }

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',zIndex:60,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}
      onClick={e=>{ if(e.target===e.currentTarget) onClose() }}>
      <div style={{background:'#fff',borderRadius:14,width:520,maxWidth:'100%',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 24px 70px rgba(0,0,0,.25)'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'18px 22px',borderBottom:'1px solid #F3F4F6'}}>
          <p style={{fontWeight:700,fontSize:16,color:'#0F1017'}}>تعديل بند BOQ</p>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'#9CA3AF',fontSize:18}}>✕</button>
        </div>

        <div style={{padding:22,display:'flex',flexDirection:'column',gap:16}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            <div>
              <label style={labelStyle}>Item No</label>
              <input style={fieldStyle} value={form.item_no} onChange={e=>h('item_no', e.target.value)} placeholder="1.1"/>
            </div>
            <div>
              <label style={labelStyle}>Unit</label>
              <input style={fieldStyle} value={form.unit} onChange={e=>h('unit', e.target.value)} placeholder="m³"/>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Description</label>
            <textarea style={{...fieldStyle, minHeight:70, resize:'vertical', fontFamily:'inherit'}} value={form.description} onChange={e=>h('description', e.target.value)}/>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            <div>
              <label style={labelStyle}>Trade</label>
              <select style={fieldStyle} value={form.package_name} onChange={e=>h('package_name', e.target.value)}>
                <option value="">— Trade —</option>
                {trades.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Cost Code</label>
              <select style={fieldStyle} value={form.cost_code} onChange={e=>h('cost_code', e.target.value)}>
                <option value="">— Cost Code —</option>
                {costCodesList.map(c => <option key={c.code} value={c.code}>{c.code}{c.description ? ` — ${c.description}` : ''}</option>)}
              </select>
            </div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            <div>
              <label style={labelStyle}>Qty</label>
              <input style={{...fieldStyle,textAlign:'right'}} type="number" value={form.qty_tendered} onChange={e=>h('qty_tendered', e.target.value)}/>
            </div>
            <div>
              <label style={labelStyle}>Selling Unit Rate</label>
              <input style={{...fieldStyle,textAlign:'right'}} type="number" value={form.selling_unit_rate} onChange={e=>h('selling_unit_rate', e.target.value)}/>
            </div>
          </div>

          <div style={{background:'#F5F1EA',borderRadius:10,padding:'12px 16px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontSize:12,fontWeight:600,color:'#6B7280'}}>Selling Price (محسوب تلقائي)</span>
            <span style={{fontSize:16,fontWeight:800,color:'#1A7A4A'}}>{total.toLocaleString('en-US',{minimumFractionDigits:2})}</span>
          </div>
        </div>

        <div style={{display:'flex',gap:10,padding:'16px 22px',borderTop:'1px solid #F3F4F6'}}>
          <button onClick={onClose} style={{flex:1,padding:'10px 0',border:'1px solid #E5E7EB',borderRadius:9,background:'#fff',color:'#6B7280',fontWeight:600,fontSize:13,cursor:'pointer'}}>إلغاء</button>
          <button onClick={handleSave} disabled={saving} style={{flex:1,padding:'10px 0',border:'none',borderRadius:9,background:'#1A7A4A',color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer',opacity:saving?0.6:1}}>
            {saving ? 'جارٍ الحفظ...' : 'حفظ التعديلات'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Cost Analysis Tab: Selling (BOQ) vs Budget vs EAC, per Cost Code ───────────
const CostAnalysisTab = ({ data, loading }) => {
  const totals = data.reduce((acc, r) => {
    acc.selling += r.selling; acc.bac += r.bac; acc.eac += r.eac
    acc.marginBudget += r.marginBudget
    if (r.hasEac) acc.marginEac += r.marginEac
    return acc
  }, { selling:0, bac:0, eac:0, marginBudget:0, marginEac:0 })
  const anyEac = data.some(r => r.hasEac)
  const totalMarginBudgetPct = totals.selling > 0 ? (totals.marginBudget/totals.selling)*100 : 0
  const totalMarginEacPct = anyEac && totals.selling > 0 ? (totals.marginEac/totals.selling)*100 : null
  const totalBudgetUtilPct = anyEac && totals.bac > 0 ? (totals.eac/totals.bac)*100 : null

  const statusDot = { green:'#1A7A4A', amber:'#D97706', red:'#DC2626', gray:'#9CA3AF' }
  const statusLabel = { green:'Healthy', amber:'Watch', red:'Loss', gray:'No Data' }

  const th = (title, subtitle, align='right') => (
    <th style={{textAlign:align, padding:'10px 12px', verticalAlign:'top'}}>
      <div style={{fontSize:11.5, fontWeight:700, color:'#374151', textTransform:'uppercase', letterSpacing:'.03em'}}>{title}</div>
      <div style={{fontSize:9.5, fontWeight:400, color:'#9CA3AF', marginTop:2, textTransform:'none', letterSpacing:0}}>{subtitle}</div>
    </th>
  )

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
      <div style={{padding:'14px 16px', borderBottom:'1px solid #F3F4F6', background:'#F9FAFB'}}>
        <p style={{fontSize:13, fontWeight:700, color:'#0F1017'}}>Cost Analysis by Cost Code</p>
        <p style={{fontSize:11, color:'#6B7280', marginTop:2}}>Compares the contract selling price (BOQ) against internal Budget and the latest EAC forecast (from Cost Control), at parent Cost Code level.</p>
      </div>

      {loading ? (
        <div style={{padding:40, textAlign:'center', color:'#9CA3AF', fontSize:13}}>Loading...</div>
      ) : data.length === 0 ? (
        <div style={{padding:40, textAlign:'center', color:'#9CA3AF', fontSize:13}}>
          No data yet. Make sure BOQ items have a Cost Code assigned, and Budget/EAC data exists for this project.
        </div>
      ) : (
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%', borderCollapse:'collapse', fontSize:12.5}}>
            <thead style={{background:'#F9FAFB', borderBottom:'2px solid #E5E7EB'}}>
              <tr>
                {th('Cost Code','Parent-level code','left')}
                {th('Selling (BOQ)','Total contract price from BOQ items')}
                {th('Budget','Internal approved budget (Direct Budget)')}
                {th('EAC','Latest estimated final cost (Cost Control)')}
                {th('Margin on Budget','Selling minus Budget — planned profit')}
                {th('Margin on Budget %','Planned profit as % of Selling')}
                {th('Margin on EAC','Selling minus EAC — expected real profit')}
                {th('Margin on EAC %','Expected real profit as % of Selling')}
                {th('Budget Utilization %','EAC ÷ Budget — are we exceeding internal budget?')}
                {th('Status','Health flag based on Margin on EAC %','center')}
              </tr>
            </thead>
            <tbody>
              {data.map(r => (
                <tr key={r.code} style={{borderBottom:'1px solid #F3F4F6'}}>
                  <td style={{padding:'9px 12px'}}>
                    <div style={{fontWeight:700, color:'#1A7A4A', fontFamily:'monospace', fontSize:12}}>{r.code}</div>
                    <div style={{fontSize:11, color:'#6B7280'}}>{r.description}</div>
                  </td>
                  <td style={{padding:'9px 12px', textAlign:'right', fontWeight:600}}>{fmt(r.selling)}</td>
                  <td style={{padding:'9px 12px', textAlign:'right', color:'#374151'}}>{fmt(r.bac)}</td>
                  <td style={{padding:'9px 12px', textAlign:'right', color:'#374151'}}>{r.hasEac ? fmt(r.eac) : '—'}</td>
                  <td style={{padding:'9px 12px', textAlign:'right', fontWeight:600, color:r.marginBudget<0?'#DC2626':'#1A7A4A'}}>{fmt(r.marginBudget)}</td>
                  <td style={{padding:'9px 12px', textAlign:'right', color:r.marginBudgetPct<0?'#DC2626':'#6B7280'}}>{r.marginBudgetPct.toFixed(1)}%</td>
                  <td style={{padding:'9px 12px', textAlign:'right', fontWeight:600, color:r.hasEac&&r.marginEac<0?'#DC2626':'#1A7A4A'}}>{r.hasEac ? fmt(r.marginEac) : '—'}</td>
                  <td style={{padding:'9px 12px', textAlign:'right', color:r.hasEac&&r.marginEacPct<0?'#DC2626':'#6B7280'}}>{r.hasEac ? r.marginEacPct.toFixed(1)+'%' : '—'}</td>
                  <td style={{padding:'9px 12px', textAlign:'right', color:r.hasEac&&r.budgetUtilPct>100?'#DC2626':'#6B7280'}}>{r.hasEac&&r.bac>0?r.budgetUtilPct.toFixed(1)+'%':'—'}</td>
                  <td style={{padding:'9px 12px', textAlign:'center'}}>
                    <span style={{display:'inline-flex',alignItems:'center',gap:5,padding:'3px 9px',borderRadius:12,fontSize:10.5,fontWeight:700,background:statusDot[r.status]+'1A',color:statusDot[r.status]}}>
                      <span style={{width:6,height:6,borderRadius:'50%',background:statusDot[r.status],display:'inline-block'}}/>
                      {statusLabel[r.status]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot style={{background:'#F9FAFB', borderTop:'2px solid #E5E7EB'}}>
              <tr>
                <td style={{padding:'10px 12px', fontWeight:700, fontSize:11, textTransform:'uppercase', color:'#374151'}}>Total</td>
                <td style={{padding:'10px 12px', textAlign:'right', fontWeight:800}}>{fmt(totals.selling)}</td>
                <td style={{padding:'10px 12px', textAlign:'right', fontWeight:700}}>{fmt(totals.bac)}</td>
                <td style={{padding:'10px 12px', textAlign:'right', fontWeight:700}}>{anyEac ? fmt(totals.eac) : '—'}</td>
                <td style={{padding:'10px 12px', textAlign:'right', fontWeight:800, color:totals.marginBudget<0?'#DC2626':'#1A7A4A'}}>{fmt(totals.marginBudget)}</td>
                <td style={{padding:'10px 12px', textAlign:'right', fontWeight:700}}>{totalMarginBudgetPct.toFixed(1)}%</td>
                <td style={{padding:'10px 12px', textAlign:'right', fontWeight:800, color:anyEac&&totals.marginEac<0?'#DC2626':'#1A7A4A'}}>{anyEac ? fmt(totals.marginEac) : '—'}</td>
                <td style={{padding:'10px 12px', textAlign:'right', fontWeight:700}}>{totalMarginEacPct!=null ? totalMarginEacPct.toFixed(1)+'%' : '—'}</td>
                <td style={{padding:'10px 12px', textAlign:'right', fontWeight:700, color:totalBudgetUtilPct>100?'#DC2626':'#374151'}}>{totalBudgetUtilPct!=null ? totalBudgetUtilPct.toFixed(1)+'%' : '—'}</td>
                <td/>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}

export default function BOQ({ initProject }) {
  const [projects, setProjects] = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  useEffect(() => { if (initProject) setSelectedProject(initProject) }, [initProject?.id])
  const { can } = useProjectAuth(selectedProject?.id)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedPackage, setSelectedPackage] = useState('all') // 'all' أو اسم trade
  const [uploadPreview, setUploadPreview] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [toast, setToast] = useState(null)
  const [selected, setSelected] = useState(new Set())
  const [trades, setTrades] = useState(['Civil','Structural','Architectural','MEP','Electrical','Mechanical','Plumbing','Landscaping','Infrastructure','Finishing'])
  const [tradesModal, setTradesModal] = useState(false)
  const [newTrade, setNewTrade] = useState('')
  const [addingRow, setAddingRow] = useState(false)
  const [newRow, setNewRow] = useState({ item_no:'', description:'', unit:'', qty_tendered:'', selling_unit_rate:'', package_name:'', cost_code:'' })
  const [editModalItem, setEditModalItem] = useState(null) // item being edited in the modal
  const [costCodesList, setCostCodesList] = useState([]) // [{code, description}]
  const [activeTab, setActiveTab] = useState('boq') // 'boq' | 'analysis'
  const [analysisData, setAnalysisData] = useState([])
  const [analysisLoading, setAnalysisLoading] = useState(false)

  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  useEffect(() => {
    const _pc = getCached('projects_full')
    if (_pc) setProjects(_pc)
    else supabase.from('projects')
      .select('id, name, name_ar, currency, enable_boq_lines, contract_value, organizations(name)')
      .order('name')
      .then(({ data }) => { setProjects(data || []); if(data) setCached('projects_full', data) })
  }, [])

  const loadItems = useCallback(async (projectId) => {
    setLoading(true)
    const { data, error } = await supabase
      .from('boq_items').select('id,project_id,item_no,cost_code,description,unit,qty_tendered,selling_unit_rate,total_selling,package_name,is_section_header')
      .eq('project_id', projectId)
      .order('sort_order', { ascending: true, nullsFirst: false })
    if (error) showToast('فشل التحميل: ' + error.message, 'error')
    else setItems(data || [])
    setLoading(false)
  }, [])

  const handleProjectSelect = (p) => {
    setSelectedProject(p)
    setItems([])
    setUploadPreview(null)
    setSelected(new Set())
    setSelectedPackage('all')
    if (p) { loadItems(p.id); loadCostCodesList(p.id) }
  }

  const loadCostCodesList = async (projectId) => {
    const { data } = await supabase.from('cost_codes').select('code,description,level').eq('project_id', projectId).eq('is_active', true).eq('level', 1).order('code')
    setCostCodesList(data || [])
  }

  // ── Cost Analysis: Selling (BOQ) vs Budget vs EAC, by Cost Code (level 1, text-based match) ──
  const loadCostAnalysis = async (projectId) => {
    setAnalysisLoading(true)
    try {
      // 1. Level-1 cost codes for this project
      const { data: costCodes } = await supabase.from('cost_codes')
        .select('id,code,description').eq('project_id', projectId).eq('level', 1).eq('is_active', true).order('code')

      // 2. Level-2 children — to roll sub_code-based Budget/EAC rows up to their parent code
      const { data: childCodes } = await supabase.from('cost_codes')
        .select('id,code,parent_id').eq('project_id', projectId).eq('level', 2)
      const childIdToParentId = {}
      const codeToChildId = {}
      ;(childCodes || []).forEach(c => {
        childIdToParentId[c.id] = c.parent_id
        codeToChildId[(c.code||'').trim().toLowerCase()] = c.id
      })
      const parentIdToCode = {}
      ;(costCodes || []).forEach(c => { parentIdToCode[c.id] = c.code })

      // 3. Selling — from boq_items, grouped by cost_code TEXT (matched directly against cost_codes.code)
      const { data: boqRows } = await supabase.from('boq_items')
        .select('cost_code, qty_tendered, selling_unit_rate, total_selling')
        .eq('project_id', projectId).eq('is_section_header', false)

      // 4. Budget (BAC) — same source/filter as Direct Forecast page: tender_direct_items grouped by cost_code, non-header rows
      const { data: tenderRows } = await supabase.from('tender_direct_items')
        .select('cost_code, total_budget, is_package_header').eq('project_id', projectId).eq('is_package_header', false)

      // 5. EAC — same source/logic as Direct Forecast page: forecast_history rows matching the active forecast_date
      const { data: activeDateRow } = await supabase.from('forecast_active_date')
        .select('forecast_date').eq('project_id', projectId).eq('source', 'direct').maybeSingle()
      const activeDate = activeDateRow?.forecast_date || null

      let eacData = []
      if (activeDate) {
        const { data } = await supabase.from('forecast_history')
          .select('row_key, sub_code, cost_code, eac_amount, forecast_date')
          .eq('project_id', projectId).eq('source', 'direct').eq('forecast_date', activeDate)
        eacData = data || []
      }

      // ===== Aggregate by parent cost code (keyed by code TEXT, since BOQ stores text) =====
      const agg = {} // code -> { bac, eac, selling }
      const ensure = (code) => { if (!agg[code]) agg[code] = { bac: 0, eac: 0, selling: 0 } }
      costCodes?.forEach(c => ensure(c.code))

      const applyBudget = (rows) => {
        (rows || []).forEach(r => {
          const code = (r.cost_code || '').trim()
          if (!code) return
          ensure(code)
          agg[code].bac += parseFloat(r.total_budget) || 0
        })
      }
      applyBudget(tenderRows)

      ;(eacData || []).forEach(r => {
        const amt = parseFloat(r.eac_amount) || 0
        if (!amt) return
        // Prefer the direct cost_code column (parent-level EAC entries store it directly)
        let parentCode = (r.cost_code || '').trim() || null
        // Otherwise roll up via sub_code -> child cost_codes -> parent
        if (!parentCode && r.sub_code) {
          const childId = codeToChildId[r.sub_code.trim().toLowerCase()]
          const parentId = childId ? childIdToParentId[childId] : null
          parentCode = parentId ? parentIdToCode[parentId] : null
        }
        if (!parentCode || !agg[parentCode]) return
        agg[parentCode].eac += amt
      })
      // Note: EAC intentionally stays 0 if not yet forecasted — no fallback to Budget

      // Selling: boq_items.cost_code is plain text — match directly to cost_codes.code
      ;(boqRows || []).forEach(r => {
        const code = (r.cost_code || '').trim()
        if (!code || !agg[code]) return
        const selling = parseFloat(r.total_selling) || (parseFloat(r.qty_tendered) * parseFloat(r.selling_unit_rate)) || 0
        agg[code].selling += selling
      })

      const rows = (costCodes || []).map(c => {
        const a = agg[c.code] || { bac: 0, eac: 0, selling: 0 }
        const hasEac = a.eac > 0
        const marginBudget = a.selling - a.bac
        const marginBudgetPct = a.selling > 0 ? (marginBudget / a.selling) * 100 : 0
        const marginEac = hasEac ? a.selling - a.eac : null
        const marginEacPct = hasEac && a.selling > 0 ? (marginEac / a.selling) * 100 : null
        const budgetUtilPct = hasEac && a.bac > 0 ? (a.eac / a.bac) * 100 : null
        let status = 'gray' // 'gray' = no EAC forecasted yet
        if (hasEac && a.selling > 0) {
          if (marginEacPct < 0) status = 'red'
          else if (marginEacPct < 10) status = 'amber'
          else status = 'green'
        }
        return { code: c.code, description: c.description, selling: a.selling, bac: a.bac, eac: a.eac, hasEac, marginBudget, marginBudgetPct, marginEac, marginEacPct, budgetUtilPct, status }
      }).filter(r => r.selling > 0 || r.bac > 0 || r.eac > 0)

      setAnalysisData(rows)
    } finally {
      setAnalysisLoading(false)
    }
  }

  const openAnalysisTab = () => {
    setActiveTab('analysis')
    if (selectedProject) loadCostAnalysis(selectedProject.id)
  }


  // حساب الـ packages وإجمالياتها
  const packagesData = (() => {
    const map = {}
    items.forEach(i => {
      const pkg = i.package_name || 'Unassigned'
      if (!map[pkg]) map[pkg] = { name: pkg, items: [], total: 0 }
      if (!i.is_section_header) {
        map[pkg].items.push(i)
        map[pkg].total += parseFloat(i.total_selling) || (parseFloat(i.qty_tendered) * parseFloat(i.selling_unit_rate)) || 0
      }
    })
    return Object.values(map).sort((a, b) => b.total - a.total)
  })()

  const totalBOQ = items.filter(i => !i.is_section_header).reduce((s, i) => s + (parseFloat(i.total_selling) || (parseFloat(i.qty_tendered) * parseFloat(i.selling_unit_rate)) || 0), 0)

  const filtered = items.filter(i => {
    const pkg = i.package_name || 'Unassigned'
    const matchSearch = !search ||
      String(i.item_no || '').toLowerCase().includes(search.toLowerCase()) ||
      i.description?.toLowerCase().includes(search.toLowerCase())
    const matchPkg = selectedPackage === 'all' || pkg === selectedPackage || i.is_section_header
    return matchSearch && matchPkg
  })

  const parseExcel = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' })
        const ws = wb.Sheets[wb.SheetNames[0]]
        // Auto-detect: if row 1 has 'Description' use range:0, else row 2 is headers use range:1
        const rowsTest = XLSX.utils.sheet_to_json(ws, { defval: '', range: 0 })
        const hasDesc = rowsTest.length > 0 && Object.keys(rowsTest[0]).some(k => k.toLowerCase().includes('desc'))
        const rows = hasDesc ? rowsTest : XLSX.utils.sheet_to_json(ws, { defval: '', range: 1 })
        let sortIdx = 0
        const normalized = rows.map(row => {
          const l = {}
          Object.keys(row).forEach(k => {
            l[k.toLowerCase().replace(/[\s().,-]+/g, '_').replace(/_+/g, '_').replace(/_$/, '')] = row[k]
          })
          const itemNo = String(l.item_no || l.item || '').trim()
          const desc   = String(l.description || '').trim()
          const unit   = String(l.unit || '').trim()
          const qty    = parseFloat(l.qty || l.qty_tendered || 0) || 0
          const rate   = parseFloat(l.selling_unit_rate_sar || l.selling_unit_rate || 0) || 0
          const cc     = String(l.cost_code || '').trim()
          const trade  = String(l.trade || l.package_name || l.package || '').trim()
          if (!desc || desc === 'Description') return null
          if (desc.toUpperCase().includes('GRAND TOTAL')) return null
          // Skip sub-section total rows — they're summaries, not line items
          if (desc.trim().startsWith('Total')) return null
          const isSectionHeader = !itemNo && !unit && !qty && !rate && !cc
          return {
            project_id: selectedProject.id,
            item_no: itemNo || null,
            description: desc,
            unit: unit || null,
            qty_tendered: qty,
            selling_unit_rate: rate,
            is_section_header: isSectionHeader,
            cost_code: cc || null,
            package_name: trade || null,
            sort_order: sortIdx++,
          }
        }).filter(Boolean)
        resolve(normalized)
      } catch (err) { reject(err) }
    }
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })

  const handleFileDrop = async (file) => {
    if (!file) return
    if (!selectedProject) return showToast('اختر مشروع أولاً', 'error')
    if (!selectedProject.enable_boq_lines) return showToast('BOQ غير مفعّل', 'error')
    const ext = file.name.split('.').pop().toLowerCase()
    if (!['xlsx', 'xls', 'csv'].includes(ext)) return showToast('Excel أو CSV فقط', 'error')
    try {
      const rows = await parseExcel(file)
      if (!rows.length) return showToast('لا يوجد صفوف صالحة', 'error')
      setUploadPreview(rows)
    } catch (e) { showToast('خطأ: ' + e.message, 'error') }
  }

  const handleUploadConfirm = async () => {
    setUploading(true)
    // احذف كل البيانات القديمة للمشروع
    await supabase.from('boq_items').delete().eq('project_id', selectedProject.id)
    // insert الجديدة — total_selling هو generated column بيتحسب تلقائياً
    const toInsert = uploadPreview.map(row => ({
      project_id: row.project_id,
      item_no: row.item_no,
      description: row.description,
      unit: row.unit,
      qty_tendered: row.qty_tendered,
      selling_unit_rate: row.selling_unit_rate,
      is_section_header: row.is_section_header,
      cost_code: row.cost_code,
      package_name: row.package_name,
      sort_order: row.sort_order,
    }))
    const { error } = await supabase.from('boq_items').insert(toInsert)
    setUploading(false)
    if (error) showToast('فشل الرفع: ' + error.message, 'error')
    else {
      showToast(`تم استيراد ${uploadPreview.length} بند بنجاح`)
      setUploadPreview(null)
      loadItems(selectedProject.id)
    }
  }

  const handleDeleteTrade = async (tradeName) => {
    if (!window.confirm(`حذف كل بنود "${tradeName}"؟`)) return
    const { error } = await supabase.from('boq_items')
      .delete()
      .eq('project_id', selectedProject.id)
      .eq('description_ar', tradeName)
    if (error) showToast('فشل الحذف: ' + error.message, 'error')
    else { showToast(`تم حذف ${tradeName}`); loadItems(selectedProject.id) }
  }

  const downloadTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([
      { item_no: '1.1', description: 'Excavation in ordinary soil', unit: 'M3', selling_price_qty: 1000, selling_price_rate: 50, total_selling_price: 50000, trade: 'Civil' },
      { item_no: '1.2', description: 'Plain Concrete C15', unit: 'M3', selling_price_qty: 500, selling_price_rate: 300, total_selling_price: 150000, trade: 'Civil' },
      { item_no: '2.1', description: 'Supply & Install DB Board', unit: 'No', selling_price_qty: 10, selling_price_rate: 2000, total_selling_price: 20000, trade: 'Electrical' },
      { item_no: '3.1', description: 'Supply & Install AHU', unit: 'No', selling_price_qty: 5, selling_price_rate: 15000, total_selling_price: 75000, trade: 'Mechanical' },
    ])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'BOQ')
    XLSX.writeFile(wb, 'boq_template.xlsx')
  }

  const exportBOQ = () => {
    if (!items.length) return showToast('لا يوجد بيانات للتصدير', 'error')
    const rows = items.map(i => ({
      'Item No':          i.item_no || '',
      'Description':      i.description || '',
      'Trade':            i.package_name || '',
      'Cost Code':        i.cost_code || '',
      'Unit':             i.unit || '',
      'Qty':              parseFloat(i.qty_tendered) || 0,
      'Selling Unit Rate': parseFloat(i.selling_unit_rate) || 0,
      'Total Selling':    parseFloat(i.total_selling) || (parseFloat(i.qty_tendered) * parseFloat(i.selling_unit_rate)) || 0,
    }))
    const ws = XLSX.utils.json_to_sheet(rows)
    // Column widths
    ws['!cols'] = [10,50,18,14,8,12,18,18].map(w=>({wch:w}))
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'BOQ')
    XLSX.writeFile(wb, `BOQ_${selectedProject?.name || 'Export'}.xlsx`)
  }

  const handleDeleteSelected = async () => {
    if (!selected.size) return
    if (!window.confirm(`حذف ${selected.size} بند؟ هذا لا يمكن التراجع عنه.`)) return
    const ids = [...selected]
    const { error } = await supabase.from('boq_items').delete().in('id', ids)
    if (error) { showToast('فشل الحذف: ' + error.message, 'error'); return }
    setSelected(new Set())
    loadItems(selectedProject.id)
    showToast(`تم حذف ${ids.length} بند`, 'success')
  }

  async function saveNewRow() {
    if (!newRow.description.trim()) return showToast('Description مطلوب', 'error')
    const qty = parseFloat(newRow.qty_tendered) || 0
    const rate = parseFloat(newRow.selling_unit_rate) || 0
    const { error } = await supabase.from('boq_items').insert({
      project_id: selectedProject.id,
      item_no: newRow.item_no || null,
      description: newRow.description,
      unit: newRow.unit || null,
      qty_tendered: qty,
      selling_unit_rate: rate,
      package_name: newRow.package_name || null,
      cost_code: newRow.cost_code || null,
      is_section_header: false,
      sort_order: items.length + 1
    })
    if (error) { showToast('فشل الإضافة: ' + error.message, 'error'); return }
    showToast('تم الإضافة')
    setAddingRow(false)
    setNewRow({ item_no:'', description:'', unit:'', qty_tendered:'', selling_unit_rate:'', package_name:'', cost_code:'' })
    loadItems(selectedProject.id)
  }


  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set())
    else setSelected(new Set(filtered.map(i => i.id)))
  }

  return (
    <div style={{minHeight:"100vh",background:"#F3F4F6",fontFamily:"inherit"}}>
      {/* Header */}
      <div style={{background:"#fff",borderBottom:"1px solid #E5E7EB",padding:"18px 24px 14px"}}>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:16}}>
          <div>
            <h1 className="text-xl font-extrabold text-[#0F1017] tracking-tight">BOQ — Bill of Quantities</h1>
            <p className="text-xs text-[#6B7280] mt-0.5">رفع وإدارة بنود الكميات حسب Trade</p>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={downloadTemplate}
              className="flex items-center gap-2 px-3 py-2 text-xs text-[#6B7280] border border-[#E5E7EB] rounded-lg hover:bg-[#FAFAFA]">
              <DownloadIcon /> Template
            </button>
            {items.length > 0 && (
              <button onClick={exportBOQ}
                className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-white bg-[#1A7A4A] border border-[#1A7A4A] rounded-lg hover:bg-[#15693f]">
                <DownloadIcon /> Export BOQ
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{padding:"20px 24px",display:"flex",flexDirection:"column",gap:16}}>

        {/* Project Selector */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
          <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-3">اختر المشروع</label>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {projects.length === 0 ? (
              <p className="text-sm text-[#9CA3AF]">لا يوجد مشاريع</p>
            ) : projects.map(p => (
              <button key={p.id} onClick={() => handleProjectSelect(p)}
                className={`text-left px-4 py-3 rounded-xl border transition-all ${
                  selectedProject?.id === p.id
                    ? 'border-[#1A7A4A] bg-[#F3F4F6] ring-2 ring-[#1A7A4A]/30'
                    : 'border-[#E5E7EB] hover:border-[#1A7A4A]/50 hover:bg-[#FAFAFA]'
                }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-[#0F1017]">{p.name}</p>
                    <p className="text-xs text-[#9CA3AF]">{p.organizations?.name}</p>
                  </div>
                  {!p.enable_boq_lines
                    ? <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200">بدون BOQ</span>
                    : selectedProject?.id === p.id && <span className="text-[#1A7A4A]"><CheckIcon /></span>}
                </div>
              </button>
            ))}
          </div>
        </div>

        {selectedProject && !selectedProject.enable_boq_lines && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-center">
            <p className="text-sm font-semibold text-amber-800">BOQ غير مفعّل لهذا المشروع</p>
            <p className="text-xs text-amber-600 mt-1">روح Projects ← Edit ← فعّل Enable BOQ Line Items</p>
          </div>
        )}

        {selectedProject && selectedProject.enable_boq_lines && (
          <>
            {/* ── Tabs ── */}
            <div style={{display:'flex',gap:8,borderBottom:'1px solid #E5E7EB'}}>
              <button onClick={()=>setActiveTab('boq')}
                style={{padding:'10px 4px',marginBottom:-1,fontSize:13,fontWeight:600,background:'none',border:'none',borderBottom:activeTab==='boq'?'2px solid #1A7A4A':'2px solid transparent',color:activeTab==='boq'?'#1A7A4A':'#6B7280',cursor:'pointer'}}>
                📋 BOQ Items
              </button>
              <button onClick={openAnalysisTab}
                style={{padding:'10px 4px',marginBottom:-1,fontSize:13,fontWeight:600,background:'none',border:'none',borderBottom:activeTab==='analysis'?'2px solid #1A7A4A':'2px solid transparent',color:activeTab==='analysis'?'#1A7A4A':'#6B7280',cursor:'pointer'}}>
                📊 Cost Analysis
              </button>
            </div>

            {activeTab === 'analysis' ? (
              <CostAnalysisTab data={analysisData} loading={analysisLoading}/>
            ) : (
            <>
            {/* ── Stats دايماً ظاهرة ── */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                <div className="text-xl font-extrabold text-[#0F1017] tracking-tight">{items.length}</div>
                <div className="text-xs text-[#6B7280] mt-1">إجمالي البنود</div>
              </div>
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                <div className="text-lg font-bold text-emerald-600">{fmt(selectedProject.contract_value)}</div>
                <div className="text-xs text-[#6B7280] mt-1">قيمة العقد ({selectedProject.currency})</div>
              </div>
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                <div className="text-lg font-bold text-[#1A7A4A]">{fmt(totalBOQ)}</div>
                <div className="text-xs text-[#6B7280] mt-1">إجمالي BOQ المرفوعة ({selectedProject.currency})</div>
                {selectedProject.contract_value > 0 && totalBOQ > 0 && (
                  <div className={`text-xs mt-1 font-medium ${totalBOQ > selectedProject.contract_value ? 'text-red-500' : totalBOQ == selectedProject.contract_value ? 'text-emerald-500' : 'text-amber-500'}`}>
                    {totalBOQ > selectedProject.contract_value ? '⚠️ تجاوز العقد' : totalBOQ < selectedProject.contract_value ? `متبقي ${fmt(selectedProject.contract_value - totalBOQ)}` : '✓ مطابق'}
                  </div>
                )}
              </div>
            </div>

            {/* ── Trades Summary Cards ── */}
            {packagesData.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-[#6B7280]">Trades Summary</h2>
                  <button onClick={() => setSelectedPackage('all')}
                    className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                      selectedPackage === 'all'
                        ? 'bg-[#0F1017] text-white border-indigo-600'
                        : 'text-[#6B7280] border-[#E5E7EB] hover:border-[#1A7A4A]/50'
                    }`}>
                    All Trades
                  </button>
                </div>

                {/* Trade Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {packagesData.map((trade, idx) => (
                    <div key={trade.name}
                      onClick={() => setSelectedPackage(selectedPackage === trade.name ? 'all' : trade.name)}
                      className={`bg-[#F3F4F6] rounded-xl border-2 p-4 cursor-pointer transition-all hover:shadow-md ${
                        selectedPackage === trade.name
                          ? 'border-[#1A7A4A] bg-[#F3F4F6]'
                          : 'border-[#E5E7EB] hover:border-indigo-200'
                      }`}>
                      <div className="flex items-start justify-between mb-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${tradeColors[idx % tradeColors.length]}`}>
                          {trade.name}
                        </span>
                        {can('BOQ.Delete') && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteTrade(trade.name) }}
                            className="text-slate-300 hover:text-red-500 transition-colors">
                            <TrashIcon />
                          </button>
                        )}
                      </div>
                      <div className="text-lg font-bold text-[#0F1017]">{fmt(trade.total)}</div>
                      <div className="text-xs text-[#9CA3AF] mt-0.5">{trade.items.length} بند</div>
                      {/* Progress bar نسبة من الإجمالي */}
                      <div className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-indigo-400 rounded-full"
                          style={{ width: `${totalBOQ ? (trade.total / totalBOQ) * 100 : 0}%` }}
                        />
                      </div>
                      <div className="text-xs text-[#9CA3AF] mt-1">
                        {totalBOQ ? ((trade.total / totalBOQ) * 100).toFixed(1) : 0}%
                      </div>
                    </div>
                  ))}
                </div>

                {/* Total BOQ Badge */}
                <div className="mt-3 bg-[#0F1017] rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-indigo-200 font-medium uppercase tracking-wide">إجمالي BOQ المرفوعة</p>
                    <p className="text-2xl font-bold text-white mt-1">{fmt(totalBOQ)} <span className="text-sm font-normal text-indigo-200">{selectedProject.currency}</span></p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-indigo-200">Contract Value</p>
                    <p className="text-lg font-semibold text-white">{fmt(selectedProject.contract_value)} <span className="text-xs font-normal text-indigo-200">{selectedProject.currency}</span></p>
                    {selectedProject.contract_value > 0 && (
                      <p className={`text-xs mt-0.5 font-medium ${
                        totalBOQ > selectedProject.contract_value ? 'text-red-300' :
                        totalBOQ === selectedProject.contract_value ? 'text-emerald-300' : 'text-amber-300'
                      }`}>
                        {totalBOQ > selectedProject.contract_value ? '⚠️ تجاوز Contract Value' :
                         totalBOQ < selectedProject.contract_value
                           ? `متبقي ${fmt(selectedProject.contract_value - totalBOQ)}`
                           : '✓ مطابق'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Upload Zone */}
            {!uploadPreview ? (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFileDrop(e.dataTransfer.files[0]) }}
                className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all
                  ${dragOver ? 'border-indigo-400 bg-[#F3F4F6]' : 'border-slate-300 bg-white hover:border-[#1A7A4A]/50'}`}
                onClick={() => document.getElementById('boq-file-input').click()}
              >
                <input id="boq-file-input" type="file" accept=".xlsx,.xls,.csv" className="hidden"
                  onChange={e => handleFileDrop(e.target.files[0])} />
                <div className="flex flex-col items-center gap-3">
                  <div className="p-3 rounded-full bg-slate-100"><UploadIcon /></div>
                  <div>
                    <p className="text-sm font-medium text-[#2E2C29]">ارفع مقايسة جديدة</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">
                      columns: <span className="font-medium text-[#6B7280]">item_no, description, unit, selling_price_qty, selling_price_rate, total_selling_price, trade</span>
                    </p>
                    <p className="text-xs text-emerald-600 mt-1 font-medium">✓ لو رفعت trade موجود هيتحدث — لو trade جديد هيتضاف</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-[#F3F4F6] bg-amber-50">
                  <div>
                    <p className="text-sm font-semibold text-amber-800">معاينة — {uploadPreview.length} بند</p>
                    <div className="flex gap-2 mt-1 flex-wrap">
                      {[...new Set(uploadPreview.map(r => r.description_ar).filter(Boolean))].map((t, i) => (
                        <span key={t} className={`text-xs px-2 py-0.5 rounded-full border ${tradeColors[i % tradeColors.length]}`}>{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setUploadPreview(null)}
                      className="px-3 py-1.5 text-xs border border-[#E5E7EB] rounded-lg text-[#6B7280] hover:bg-[#FAFAFA]">إلغاء</button>
                    <button onClick={handleUploadConfirm} disabled={uploading}
                      className="px-4 py-1.5 text-xs font-medium text-white bg-[#1A7A4A] rounded-lg hover:bg-[#15693f] disabled:opacity-50">
                      {uploading ? 'جاري الاستيراد...' : `استيراد ${uploadPreview.length} بند`}
                    </button>
                  </div>
                </div>
                <div className="overflow-auto max-h-64">
                  <table className="w-full text-xs">
                    <thead className="bg-[#F3F4F6] sticky top-0">
                      <tr>{['Item No','Description','Trade','Unit','Qty','Rate','Total'].map(h => (
                        <th key={h} className="text-left px-3 py-2 font-medium text-[#6B7280]">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6]">
                      {uploadPreview.slice(0, 20).map((row, i) => (
                        <tr key={i} className="hover:bg-[#FAFAFA]">
                          <td className="px-3 py-2 font-mono">{row.item_no || '—'}</td>
                          <td className="px-3 py-2 max-w-xs truncate text-[#6B7280]">{row.description}</td>
                          <td className="px-3 py-2">
                            {row.description_ar
                              ? <span className="px-2 py-0.5 rounded-full bg-[#D1D5DB] text-[#0F1017] text-xs">{row.description_ar}</span>
                              : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-3 py-2">{row.unit || '—'}</td>
                          <td className="px-3 py-2 text-right">{fmt(row.qty_tendered)}</td>
                          <td className="px-3 py-2 text-right">{fmt(row.selling_unit_rate)}</td>
                          <td className="px-3 py-2 text-right font-medium text-[#1A7A4A]">{fmt(row.total_selling)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Items Table */}
            {items.length > 0 && (
              <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-[#F3F4F6]">
                  <div className="relative flex-1 min-w-48">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none"><SearchIcon /></span>
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="بحث في البنود..."
                      className="w-full pl-9 pr-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]" />
                  </div>
                  {/* Trade tabs */}
                  <div className="flex gap-1 flex-wrap">
                    <button onClick={() => setSelectedPackage('all')}
                      className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                        selectedPackage === 'all' ? 'bg-[#0F1017] text-white border-indigo-600' : 'text-[#6B7280] border-[#E5E7EB] hover:bg-[#FAFAFA]'
                      }`}>All</button>
                    {packagesData.map((trade, idx) => (
                      <button key={trade.name} onClick={() => setSelectedPackage(selectedPackage === trade.name ? 'all' : trade.name)}
                        className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                          selectedPackage === trade.name
                            ? 'bg-[#0F1017] text-white border-indigo-600'
                            : 'text-[#6B7280] border-[#E5E7EB] hover:bg-[#FAFAFA]'
                        }`}>
                        {trade.name}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    <button onClick={()=>setTradesModal(true)}
                      className="px-3 py-1.5 text-xs border border-[#E5E7EB] rounded-lg text-[#6B7280] hover:bg-[#FAFAFA]">⚙ Trades</button>
                    {can('BOQ.Create') && (
                      <button onClick={()=>setAddingRow(true)}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-[#1A7A4A] rounded-lg hover:bg-[#15693f]">+ Add Row</button>
                    )}
                    {selected.size > 0 && can('BOQ.Delete') && (
                      <button onClick={handleDeleteSelected}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors">
                        🗑 حذف {selected.size} بند
                      </button>
                    )}
                    <span className="text-xs text-[#9CA3AF]">{filtered.length} بند</span>
                  </div>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-16 text-[#9CA3AF]">
                    <div className="w-6 h-6 border-2 border-[#1A7A4A]/50 border-t-indigo-600 rounded-full animate-spin mr-3" />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                        <tr>
                          <th className="w-10 px-4 py-3">
                            <input type="checkbox"
                              checked={selected.size === filtered.length && filtered.length > 0}
                              onChange={toggleAll} className="w-4 h-4 text-[#1A7A4A] rounded" />
                          </th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Item No</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Description</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Trade</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Cost Code</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Unit</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Qty</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Selling Unit Rate</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Selling Price (SAR)</th>
                          <th className="text-center px-3 py-3 text-xs font-semibold text-[#6B7280] uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F3F4F6]">
                        {filtered.map((item, idx) => {
                          // Section header row — detect by flag or by no item_no + no qty + no rate
                          const isSectionHeader = item.is_section_header || (!item.item_no && !item.qty_tendered && !item.selling_unit_rate)
                          if (isSectionHeader) return (
                            <tr key={item.id}>
                              <td className="px-4 py-2.5 bg-[#2E2C29]">
                                <input type="checkbox" checked={selected.has(item.id)}
                                  onChange={() => { const s = new Set(selected); s.has(item.id) ? s.delete(item.id) : s.add(item.id); setSelected(s) }}
                                  className="w-4 h-4 rounded opacity-50" />
                              </td>
                              <td colSpan={6} className="px-4 py-2.5 bg-[#2E2C29]">
                                <span className="text-xs font-bold text-white uppercase tracking-wider">{item.description}</span>
                              </td>
                              <td className="px-4 py-2.5 bg-[#2E2C29] text-right">
                                <span className="text-xs font-mono text-slate-400">{item.cost_code || ''}</span>
                              </td>
                              <td className="px-4 py-2.5 bg-[#2E2C29]"/>
                              <td className="px-4 py-2.5 bg-[#2E2C29]"/>
                            </tr>
                          )
                          const selling = parseFloat(item.total_selling) || (parseFloat(item.qty_tendered) * parseFloat(item.selling_unit_rate)) || 0
                          return (
                            <tr key={item.id} className={`hover:bg-[#FAFAFA] transition-colors ${selected.has(item.id) ? 'bg-[#F3F4F6]/50' : ''}`}>
                              <td className="px-4 py-3">
                                <input type="checkbox" checked={selected.has(item.id)}
                                  onChange={() => { const s = new Set(selected); s.has(item.id) ? s.delete(item.id) : s.add(item.id); setSelected(s) }}
                                  className="w-4 h-4 text-[#1A7A4A] rounded" />
                              </td>
                              <td className="px-2 py-2 font-mono text-xs font-bold text-[#2E2C29]">{item.item_no || '—'}</td>
                              <td className="px-2 py-2 text-sm text-[#2E2C29] max-w-xs">{item.description}</td>
                              <td className="px-2 py-2">
                                {item.package_name
                                  ? <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#F3F4F6] text-[#1A7A4A] border border-[#1A7A4A]/20">{item.package_name}</span>
                                  : <span className="text-slate-300 text-xs">—</span>
                                }
                              </td>
                              <td className="px-2 py-2 font-mono text-xs text-[#1A7A4A] font-semibold">{item.cost_code || '—'}</td>
                              <td className="px-2 py-2 text-xs text-[#6B7280]">{item.unit || '—'}</td>
                              <td className="px-2 py-2 text-right text-xs text-[#6B7280]">
                                {item.qty_tendered ? Number(item.qty_tendered).toLocaleString('en-US',{minimumFractionDigits:2}) : '—'}
                              </td>
                              <td className="px-2 py-2 text-right text-xs text-[#6B7280]">
                                {item.selling_unit_rate ? Number(item.selling_unit_rate).toLocaleString('en-US',{minimumFractionDigits:2}) : '—'}
                              </td>
                              <td className="px-2 py-2 text-right font-semibold text-sm text-[#1A7A4A]">
                                {selling ? Number(selling).toLocaleString('en-US',{minimumFractionDigits:2}) : '—'}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <div style={{display:'flex',gap:6,justifyContent:'center'}}>
                                  {can('BOQ.Edit') && (
                                    <button title="تعديل" onClick={()=>setEditModalItem(item)}
                                      style={{background:'none',border:'none',cursor:'pointer',fontSize:14,padding:'2px 4px'}}>✏️</button>
                                  )}
                                  {can('BOQ.Delete') && (
                                    <button title="حذف" onClick={async()=>{ if(window.confirm('تأكيد حذف البند؟')){ await supabase.from('boq_items').delete().eq('id',item.id); showToast('تم الحذف'); loadItems(selectedProject.id) } }}
                                    style={{background:'none',border:'none',cursor:'pointer',fontSize:14,padding:'2px 4px'}}>🗑️</button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                      {addingRow && (
                        <tbody>
                          <tr style={{background:'#F0FDF4'}}>
                            <td className="px-2 py-2"><button onClick={()=>setAddingRow(false)} style={{color:'#9CA3AF',background:'none',border:'none',cursor:'pointer',fontSize:12}}>✕</button></td>
                            <td className="px-2 py-2"><input placeholder="Item No" style={{padding:'3px 6px',border:'1px solid #D1D5DB',borderRadius:4,fontSize:11,width:70}} value={newRow.item_no} onChange={e=>setNewRow(p=>({...p,item_no:e.target.value}))}/></td>
                            <td className="px-2 py-2"><input placeholder="Description *" style={{padding:'3px 6px',border:'1px solid #D1D5DB',borderRadius:4,fontSize:11,width:180}} value={newRow.description} onChange={e=>setNewRow(p=>({...p,description:e.target.value}))}/></td>
                            <td className="px-2 py-2">
                              <select style={{padding:'3px 6px',border:'1px solid #D1D5DB',borderRadius:4,fontSize:11,width:110}} value={newRow.package_name} onChange={e=>setNewRow(p=>({...p,package_name:e.target.value}))}>
                                <option value="">— Trade —</option>
                                {trades.map(t=><option key={t} value={t}>{t}</option>)}
                              </select>
                            </td>
                            <td className="px-2 py-2">
                              <select style={{padding:'3px 6px',border:'1px solid #D1D5DB',borderRadius:4,fontSize:11,width:110}} value={newRow.cost_code} onChange={e=>setNewRow(p=>({...p,cost_code:e.target.value}))}>
                                <option value="">— Cost Code —</option>
                                {costCodesList.map(c=><option key={c.code} value={c.code}>{c.code}</option>)}
                              </select>
                            </td>
                            <td className="px-2 py-2"><input placeholder="Unit" style={{padding:'3px 6px',border:'1px solid #D1D5DB',borderRadius:4,fontSize:11,width:50}} value={newRow.unit} onChange={e=>setNewRow(p=>({...p,unit:e.target.value}))}/></td>
                            <td className="px-2 py-2"><input placeholder="Qty" type="number" style={{padding:'3px 6px',border:'1px solid #D1D5DB',borderRadius:4,fontSize:11,width:70,textAlign:'right'}} value={newRow.qty_tendered} onChange={e=>setNewRow(p=>({...p,qty_tendered:e.target.value}))}/></td>
                            <td className="px-2 py-2"><input placeholder="Rate" type="number" style={{padding:'3px 6px',border:'1px solid #D1D5DB',borderRadius:4,fontSize:11,width:90,textAlign:'right'}} value={newRow.selling_unit_rate} onChange={e=>setNewRow(p=>({...p,selling_unit_rate:e.target.value}))}/></td>
                            <td className="px-2 py-2 text-right">
                              <button onClick={saveNewRow} style={{padding:'3px 10px',background:'#1A7A4A',color:'#fff',border:'none',borderRadius:4,fontSize:11,cursor:'pointer',fontWeight:700}}>Save</button>
                            </td>
                          </tr>
                        </tbody>
                      )}
                      <tfoot className="bg-[#F3F4F6] border-t-2 border-slate-300">
                        <tr>
                          <td colSpan={7} className="px-4 py-3 text-xs font-bold text-[#6B7280] uppercase">
                            {selectedPackage === 'all' ? 'إجمالي BOQ' : `إجمالي ${selectedPackage}`}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-bold text-[#1A7A4A]">
                            {fmt(filtered.reduce((s, i) => s + (parseFloat(i.total_selling) || (parseFloat(i.qty_tendered) * parseFloat(i.selling_unit_rate)) || 0), 0))}
                          </td>
                          <td/>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}
            </>
            )}
          </>
        )}
      </div>

      {/* Edit Item Modal */}
      {editModalItem && (
        <EditItemModal
          item={editModalItem}
          trades={trades}
          costCodesList={costCodesList}
          onClose={()=>setEditModalItem(null)}
          onSaved={()=>{ setEditModalItem(null); loadItems(selectedProject.id) }}
          showToast={showToast}
        />
      )}

      {/* Manage Trades Modal */}
      {tradesModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',zIndex:50,display:'flex',alignItems:'center',justifyContent:'center'}}
          onClick={e=>{if(e.target===e.currentTarget)setTradesModal(false)}}>
          <div style={{background:'#fff',borderRadius:12,padding:24,width:360,boxShadow:'0 20px 60px rgba(0,0,0,.2)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <p style={{fontWeight:700,fontSize:15,color:'#0F1017'}}>Manage Trades</p>
              <button onClick={()=>setTradesModal(false)} style={{background:'none',border:'none',cursor:'pointer',color:'#9CA3AF',fontSize:16}}>✕</button>
            </div>
            <div style={{display:'flex',gap:8,marginBottom:14}}>
              <input value={newTrade} onChange={e=>setNewTrade(e.target.value)}
                onKeyDown={e=>{ if(e.key==='Enter'&&newTrade.trim()){ setTrades(p=>[...p,newTrade.trim()]); setNewTrade('') }}}
                placeholder="Trade name..." style={{flex:1,padding:'7px 10px',border:'1px solid #D1D5DB',borderRadius:6,fontSize:12,outline:'none'}}/>
              <button onClick={()=>{ if(newTrade.trim()){ setTrades(p=>[...p,newTrade.trim()]); setNewTrade('') }}}
                style={{padding:'7px 14px',background:'#1A7A4A',color:'#fff',border:'none',borderRadius:6,fontSize:12,cursor:'pointer',fontWeight:700}}>+ Add</button>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:6,maxHeight:280,overflowY:'auto'}}>
              {trades.map(t=>(
                <div key={t} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'7px 10px',background:'#F9FAFB',borderRadius:6,border:'1px solid #E5E7EB'}}>
                  <span style={{fontSize:12,color:'#0F1017',fontWeight:500}}>{t}</span>
                  <button onClick={()=>setTrades(p=>p.filter(x=>x!==t))}
                    style={{background:'none',border:'none',cursor:'pointer',color:'#DC2626',fontSize:12,padding:'0 4px'}}>✕</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  )
}
